import { createAsyncThunk } from '@reduxjs/toolkit';
import { auth, googleProvider } from '@/services/firebase';
import api from '@/services/axios';
import { FirebaseUser, User } from '@/models';
import { USER_ROLES } from '@/constants/roles';
import { ALLOWED_EMAIL_DOMAINS, isAllowedEmailDomain } from '@/constants/auth';
import { USER_ROLE } from '@skyrise-eng/types';
import { resetAllApiState } from '@/api/registry';

const BYPASS_ROLE_CHECK = import.meta.env.VITE_BYPASS_ROLE_CHECK === 'true';
const FALLBACK_ROLE = USER_ROLE.ADMIN;

const DEFAULT_ERROR_MESSAGE = 'Something went wrong.';

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE;

const populateUserWithRole = async (firebaseUser: FirebaseUser): Promise<User> => {
  const user: FirebaseUser = firebaseUser.toJSON() as User;
  const token: string = await firebaseUser.getIdToken();

  const baseUrl = import.meta.env.VITE_API_URL;
  if (!baseUrl || typeof baseUrl !== 'string' || baseUrl.trim() === '') {
    throw new Error(
      'VITE_API_URL is not set. Add VITE_API_URL=http://localhost:3000/develop to your .env (backend base URL) and restart the dev server (npm run dev).'
    );
  }

  let role: string;
  try {
    const response = await api('/users/api/v1', {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = response?.data;
    if (typeof data === 'string' && data.trim().startsWith('<')) {
      throw new Error(
        'The role request returned the app page instead of the API. Set VITE_API_URL in .env to your backend (e.g. http://localhost:3000/develop), then restart the dev server.'
      );
    }
    role = response.data?.role;
  } catch (err: unknown) {
    const msg =
      err && typeof err === 'object' && 'message' in err
        ? String((err as { message: unknown }).message)
        : 'Request failed';
    throw new Error(msg || 'Permission denied');
  }

  if (role == null || role === '') {
    throw new Error(
      'Permission denied: no role returned from backend. Check that GET /users/api/v1 returns { role: "ADMIN" | "SUPER_ADMIN" | ... }.'
    );
  }

  if (!USER_ROLES.includes(role as USER_ROLE)) {
    if (BYPASS_ROLE_CHECK) {
      role = FALLBACK_ROLE;
    } else {
      throw new Error(
        `Permission denied: role "${role}" is not allowed. Allowed roles: ${USER_ROLES.join(', ')}. ` +
          'Ensure the backend returns one of these, or add this role in src/constants/roles.ts (USER_ROLES). ' +
          'For local dev you can set VITE_BYPASS_ROLE_CHECK=true in .env to use a fallback role.'
      );
    }
  }

  return { ...user, role: role as USER_ROLE };
};

/**
 * Signs out of Firebase and drops every cached API response, so nothing from
 * this session is served to whoever signs in next on the same browser.
 */
export const signOut = createAsyncThunk<void, void, { rejectValue: string }>(
  'auth/signOut',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await auth.signOut();
      resetAllApiState(dispatch);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

/** Firebase codes for a popup the user closed or that a newer popup replaced — not failures. */
const POPUP_DISMISSED_CODES = new Set(['auth/popup-closed-by-user', 'auth/cancelled-popup-request']);

/**
 * Opens the Google sign-in popup.
 *
 * Only authenticates with Firebase. Loading the user and their role is left to
 * the auth listener in `App`, which dispatches {@link signIn} when Firebase
 * reports the new user — one path for both a fresh sign-in and a restored
 * session, so the role is never fetched twice.
 *
 * Closing the popup is not reported as an error.
 */
export const signInWithGoogle = createAsyncThunk<void, void, { rejectValue: string | null }>(
  'auth/signInWithGoogle',
  async (_, { rejectWithValue }) => {
    try {
      await auth.signInWithPopup(googleProvider);
    } catch (error) {
      const code = (error as { code?: string } | null)?.code;
      return rejectWithValue(code && POPUP_DISMISSED_CODES.has(code) ? null : getErrorMessage(error));
    }
  }
);

const DOMAIN_DENIED_MESSAGE =
  `Use your ${ALLOWED_EMAIL_DOMAINS.map((domain) => `@${domain}`).join(' or ')} account to sign in.`;

/**
 * Loads the app user for an authenticated Firebase user: checks the email
 * domain, then fetches their role from the backend.
 *
 * @param firebaseUser - The user Firebase reports as signed in.
 */
export const signIn = createAsyncThunk<User, FirebaseUser, { rejectValue: string }>(
  'auth/signIn',
  async (firebaseUser, { rejectWithValue }) => {

    // Convenience check only — the backend is what actually grants access.
    // Sign the account straight back out so an outside address is not left
    // holding a live Firebase session against this project.
    if (!isAllowedEmailDomain(firebaseUser.email)) {
      await auth.signOut().catch(() => undefined);
      return rejectWithValue(DOMAIN_DENIED_MESSAGE);
    }

    try {
      return await populateUserWithRole(firebaseUser);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);
